import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiHandler } from '../../../api/apihandler';
import { FAVORITE_RECIPE_LIST, ADD_FAVORITE_RECIPE } from '../../../api/constants';
import { apiSucceeded, getAvatarColor, getInitials } from '../feed/feedUtils';
import PeopleYouMayKnowWidget from '../../../components/nemonto/PeopleYouMayKnowWidget';
import MyStatsWidget from '../../../components/nemonto/MyStatsWidget';
import PromoCard from '../../../components/nemonto/PromoCard';
import EmptyState from '../../../components/nemonto/EmptyState';
import MediaPlaceholderIcon from '../../../components/nemonto/MediaPlaceholderIcon';
import './social-list-page.css';

const Avatar = ({ url, name, className }) => {
  if (url) return <img src={url} alt={name} className={className} />;
  return (
    <div className={className} style={{ background: getAvatarColor(name || 'User'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>
      {getInitials(...(name || 'User').split(' '))}
    </div>
  );
};

const FavouriteRecipesPage = () => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState(new Set());

  async function loadFavorites() {
    setLoading(true);
    try {
      const res = await apiHandler(FAVORITE_RECIPE_LIST, 'POST', { page: 1, limit: 50 });
      if (apiSucceeded(res)) setRecipes(res?.data?.data?.userFavoriteRecipeList?.recipes || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadFavorites(); }, []);

  async function removeFavorite(recipeId) {
    if (busyIds.has(recipeId)) return;
    setBusyIds((prev) => new Set([...prev, recipeId]));
    try {
      const res = await apiHandler(ADD_FAVORITE_RECIPE, 'POST', { recipe_id: recipeId });
      if (apiSucceeded(res)) setRecipes((prev) => prev.filter((r) => r.id !== recipeId));
    } catch (e) { console.error(e); }
    finally {
      setBusyIds((prev) => { const next = new Set(prev); next.delete(recipeId); return next; });
    }
  }

  return (
    <div className="nmsl-layout">
      <div className="nmsl-main">
        <div className="nmsl-page-header">
          <div className="nmsl-header">
            <h3 className="nmsl-title">Favourite Recipes</h3>
            {recipes.length > 0 && <span className="nmsl-count-pill">{recipes.length}</span>}
          </div>
          <p className="nmsl-subtitle">Recipes you've saved to cook later.</p>
        </div>

        <div className="nmsl-main-body">
          {loading && <p className="nmsl-loading">Loading…</p>}
          {!loading && recipes.length === 0 && (
            <EmptyState icon="bookmark" title="No favourites yet" sub="Recipes you save will show up here." />
          )}

          {!loading && recipes.length > 0 && (
            <div className="nmsl-recipe-grid">
              {recipes.map((r) => {
                const image = r?.medias?.[0]?.image_path || null;
                const ownerName = `${r?.owner?.first_name || ''} ${r?.owner?.last_name || ''}`.trim() || 'User';
                const ownerPhoto = r?.owner?.user_setting?.photo || r?.owner?.photo || null;
                return (
                  <div
                    key={r.id}
                    className="nmsl-recipe-card"
                    onClick={() => navigate(`/pages/recipe/${r.id}`)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="nmsl-recipe-thumb">
                      {image ? <img src={image} alt={r.title || 'Recipe'} /> : <MediaPlaceholderIcon size={60} />}
                      <button
                        type="button"
                        className="nmsl-recipe-save-btn"
                        disabled={busyIds.has(r.id)}
                        onClick={(e) => { e.stopPropagation(); removeFavorite(r.id); }}
                        title="Remove from favourites"
                      >
                        <span className="material-symbols-outlined filled" style={{ fontSize: 16 }}>bookmark</span>
                      </button>
                    </div>
                    <div className="nmsl-recipe-body">
                      <div className="nmsl-recipe-owner-row">
                        <Avatar url={ownerPhoto} name={ownerName} className="nmsl-recipe-owner-avatar" />
                        <span className="nmsl-recipe-owner-name">{ownerName}</span>
                      </div>
                      <p className="nmsl-recipe-title">{r.title || 'Untitled recipe'}</p>
                      {r.content && <p className="nmsl-recipe-excerpt">{r.content}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <aside className="nmsl-rail">
        <PromoCard
          label="Made it"
          title="Cooked one of these yet?"
          sub="Share your version and see how other cooks made these recipes their own."
          ctaText="Explore Made It"
          to="/dashboard/app/profile"
          state={{ tab: 'madeit' }}
        />
        <PeopleYouMayKnowWidget />
        <MyStatsWidget />
      </aside>
    </div>
  );
};

export default FavouriteRecipesPage;
